import { type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Loader2 } from 'lucide-react'

import api from '@/admin/lib/api'
import { StaffShell } from '@/admin/components/staff/staff-shell'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { useFormatters } from '@/shared/lib/formatting'

interface VoiceCallDetail {
  uuid: string
  call_id: string
  tenant_name: string
  organisation_name: string
  source: string
  agent_id: string
  claim_uuid: string | null
  consumer_email: string | null
  summary: string
  outcome: string
  sentiment: string
  successful: boolean | null
  needs_human_callback: boolean
  in_voicemail: boolean | null
  custom_analysis: Record<string, unknown>
  transcript: string
  recording_url: string
  recording_stored_url: string
  recording_archived_at: string | null
  caller_number: string
  callee_number: string
  direction: string
  call_type: string
  call_status: string
  disconnection_reason: string
  started_at: string | null
  ended_at: string | null
  duration_ms: number | null
  cost_cents: string | null
  callback_created_at: string | null
  analyzed_at: string | null
  created_at: string
}

function formatDuration(ms: number | null): string {
  if (!ms || ms <= 0) return '-'
  const total = Math.round(ms / 1000)
  const m = Math.floor(total / 60)
  const s = total % 60
  return m > 0 ? `${m}m ${s}s` : `${s}s`
}

function formatCost(cents: string | null): string {
  if (cents === null || cents === '') return '-'
  const n = Number(cents)
  if (Number.isNaN(n)) return '-'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(n / 100)
}

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] p-5">
      <h2 className="clara-section-label mb-3 tracking-[0.14em] text-[var(--ink-3)]">
        {title}
      </h2>
      {children}
    </div>
  )
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b-[0.5px] border-[var(--rule)] py-2 last:border-b-0">
      <span className="text-[12px] text-[var(--ink-3)]">{label}</span>
      <span className="text-right text-[12px] text-[var(--ink)]">
        {value === null || value === '' || value === undefined ? '-' : value}
      </span>
    </div>
  )
}

export function VoiceCallDetailPage() {
  const navigate = useNavigate()
  const { uuid } = useParams<{ uuid: string }>()
  const { formatDateTime } = useFormatters()

  const { data, isPending, isError } = useQuery<VoiceCallDetail>({
    queryKey: ['staff-voice-call', uuid],
    queryFn: async () => {
      // The api client unwraps the {status, message, result} envelope, so
      // res.data is already the record.
      const res = await api.get<VoiceCallDetail>(`/staff/voice-calls/${uuid}/`)
      return res.data
    },
    enabled: Boolean(uuid),
  })

  const customEntries = data ? Object.entries(data.custom_analysis ?? {}) : []

  return (
    <StaffShell>
      <div className="mx-auto max-w-4xl px-6 py-8">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/staff/voice-calls')}
          className="mb-6 gap-1.5"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Voice Calls
        </Button>

        {isPending ? (
          <div className="flex items-center justify-center py-24 text-[var(--ink-3)]">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : isError || !data ? (
          <div className="py-24 text-center text-[12px] text-[var(--ink-3)]">
            Could not load this voice call.
          </div>
        ) : (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="clara-title">
                {data.tenant_name || 'Voice call'}
              </h1>
              {data.sentiment ? (
                <Badge
                  variant={
                    data.sentiment.toLowerCase() === 'negative'
                      ? 'destructive'
                      : data.sentiment.toLowerCase() === 'positive'
                        ? 'default'
                        : 'secondary'
                  }
                >
                  {data.sentiment}
                </Badge>
              ) : null}
              {data.needs_human_callback ? (
                <Badge variant="destructive">Callback requested</Badge>
              ) : null}
            </div>

            <Card title="Overview">
              <Row label="Call ID" value={<span className="font-mono">{data.call_id}</span>} />
              <Row label="Tenant" value={data.tenant_name} />
              <Row label="Organisation" value={data.organisation_name} />
              <Row label="Source" value={data.source} />
              <Row label="Agent ID" value={<span className="font-mono">{data.agent_id}</span>} />
              <Row label="Type" value={data.call_type} />
              <Row label="Direction" value={data.direction} />
              <Row label="Status" value={data.call_status} />
              <Row label="Disconnection" value={data.disconnection_reason} />
              <Row label="Caller" value={data.caller_number} />
              <Row label="Callee" value={data.callee_number} />
              <Row
                label="Started"
                value={data.started_at ? formatDateTime(data.started_at) : '-'}
              />
              <Row
                label="Ended"
                value={data.ended_at ? formatDateTime(data.ended_at) : '-'}
              />
              <Row label="Duration" value={formatDuration(data.duration_ms)} />
              <Row label="Cost" value={formatCost(data.cost_cents)} />
            </Card>

            <Card title="Analysis">
              <Row label="Outcome" value={data.outcome} />
              <Row
                label="Successful"
                value={
                  data.successful === null ? '-' : data.successful ? 'Yes' : 'No'
                }
              />
              <Row
                label="Needs human callback"
                value={data.needs_human_callback ? 'Yes' : 'No'}
              />
              <Row
                label="In voicemail"
                value={
                  data.in_voicemail === null ? '-' : data.in_voicemail ? 'Yes' : 'No'
                }
              />
              <div className="pt-3">
                <p className="mb-1 text-[12px] text-[var(--ink-3)]">Summary</p>
                <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-[var(--ink)]">
                  {data.summary || '-'}
                </p>
              </div>
            </Card>

            {customEntries.length > 0 ? (
              <Card title="Custom extraction">
                {customEntries.map(([key, value]) => (
                  <Row
                    key={key}
                    label={key}
                    value={
                      typeof value === 'boolean'
                        ? value
                          ? 'Yes'
                          : 'No'
                        : String(value ?? '-')
                    }
                  />
                ))}
              </Card>
            ) : null}

            <Card title="Linked records">
              <Row label="Consumer" value={data.consumer_email} />
              <Row
                label="Claim"
                value={
                  data.claim_uuid ? (
                    <Button
                      variant="link"
                      onClick={() => navigate(`/claims/${data.claim_uuid}`)}
                      className="h-auto p-0 font-mono text-[var(--green)] hover:underline"
                    >
                      {data.claim_uuid}
                    </Button>
                  ) : (
                    '-'
                  )
                }
              />
            </Card>

            {data.recording_stored_url || data.recording_url ? (
              <Card title="Recording">
                <div className="flex flex-col gap-1.5">
                  {data.recording_stored_url ? (
                    <a
                      href={data.recording_stored_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[12px] text-[var(--green)] hover:underline"
                    >
                      Download saved copy
                    </a>
                  ) : (
                    <p className="text-[11px] text-[var(--ink-3)]">
                      Saved copy is still being archived (or the provider link
                      expired before we could fetch it).
                    </p>
                  )}
                  {data.recording_url ? (
                    <a
                      href={data.recording_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[12px] text-[var(--ink-3)] hover:underline"
                    >
                      Open provider link
                    </a>
                  ) : null}
                </div>
              </Card>
            ) : null}

            {data.transcript ? (
              <Card title="Transcript">
                <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded-md bg-[var(--bg-subtle)] p-3 text-[12px] leading-relaxed text-[var(--ink)]">
                  {data.transcript}
                </pre>
              </Card>
            ) : null}
          </div>
        )}
      </div>
    </StaffShell>
  )
}
